import React, { useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, IconButton, Badge, Popover,
  List, ListItem, ListItemText, Button, Avatar,
} from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import MenuRoundedIcon           from '@mui/icons-material/MenuRounded';
import ChevronRightRoundedIcon   from '@mui/icons-material/ChevronRightRounded';
import { useConfig }             from '../../components/ConfigContext';
import { AuthContext }           from '../../components/signin/AuthContext';
import ThemePicker               from './ThemePicker';

const ROUTE_LABELS = {
  '/v3/dashboard':   'Dashboard',
  '/v3/analytics':   'Analytics',
  '/v3/config':      'Konfiguration',
  '/v3/actorconfig': 'Aktoren',
  '/v3/alertconfig': 'Benachrichtigungen',
  '/v3/grow_plan':   'Grow Plan',
  '/v3/settings':    'Einstellungen',
};

export default function TopBar({ onMenuClick }) {
  const location   = useLocation();
  const navigate   = useNavigate();
  const { changes, saveConfig } = useConfig();
  const { avatar, username }   = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const label       = ROUTE_LABELS[location.pathname] ?? 'Growr';
  const hasUnsaved  = Object.keys(changes).length > 0;

  const labelMap = {
    temperatur_tag: 'Temperatur Tag', temperatur_nacht: 'Temperatur Nacht',
    luftfeuchte_tag: 'Luftfeuchte Tag', luftfeuchte_nacht: 'Luftfeuchte Nacht',
    abluft_max_intensity: 'Abluft Intensität', abweichung_luftfeuchte: 'Abweichung Luftfeuchte',
    max_light: 'Max. Lichtintensität', dauer: 'Dauer',
    sun_rise: 'Sonnenaufgang', sun_set: 'Sonnenuntergang',
  };

  return (
    <Box
      sx={{
        height: 64,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(118,196,66,0.08)',
        background: 'rgba(4,14,5,0.6)',
        backdropFilter: 'blur(12px)',
        flexShrink: 0,
        gap: 2,
      }}
    >
      {/* Left: hamburger (mobile) + breadcrumb */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <IconButton
          size="small"
          onClick={onMenuClick}
          sx={{ display: { md: 'none' }, color: 'rgba(255,255,255,0.7)' }}
        >
          <MenuRoundedIcon />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
            Growr
          </Typography>
          <ChevronRightRoundedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.25)' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
            {label}
          </Typography>
        </Stack>
      </Stack>

      {/* Right: theme + notifications + avatar */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <ThemePicker />
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{
            color: hasUnsaved ? '#f59e0b' : 'rgba(255,255,255,0.5)',
            background: hasUnsaved ? 'rgba(245,158,11,0.1)' : 'transparent',
            border: hasUnsaved ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent',
            borderRadius: '10px',
            '&:hover': { background: 'rgba(255,255,255,0.06)' },
          }}
        >
          <Badge badgeContent={hasUnsaved ? Object.keys(changes).length : 0} color="warning">
            <NotificationsRoundedIcon fontSize="small" />
          </Badge>
        </IconButton>

        <Avatar
          src={avatar}
          alt={username}
          sx={{
            width: 34, height: 34,
            cursor: 'pointer',
            border: '2px solid rgba(118,196,66,0.3)',
            '&:hover': { border: '2px solid #76c442' },
            transition: 'border 0.18s ease',
          }}
          onClick={() => navigate('/v3/settings')}
        />
      </Stack>

      {/* Notifications popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 300,
            background: 'rgba(8,20,9,0.97)',
            border: '1px solid rgba(118,196,66,0.15)',
            backdropFilter: 'blur(20px)',
            mt: 0.5,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#76c442', fontWeight: 700 }}>
            Ungespeicherte Änderungen
          </Typography>
          <List dense disablePadding>
            {hasUnsaved ? (
              Object.entries(changes).map(([key, value]) => (
                <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={`${labelMap[key] || key} → ${value}`}
                    primaryTypographyProps={{ variant: 'body2', color: 'rgba(255,255,255,0.8)' }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem disablePadding>
                <ListItemText
                  primary="Keine Änderungen"
                  primaryTypographyProps={{ variant: 'body2', color: 'rgba(255,255,255,0.4)' }}
                />
              </ListItem>
            )}
          </List>
          {hasUnsaved && (
            <Button
              fullWidth variant="contained" color="primary"
              onClick={() => { saveConfig(); setAnchorEl(null); }}
              sx={{ mt: 1.5 }}
            >
              Alle speichern
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
